// Ink to solid shapes: whatever is painted, traced back into outlines, one per piece that holds together. Move
// works on these, so a mouth drawn as two overlapping halves, or an eye with a stroke through it, moves as one.

import type { Pt } from './geom';

export interface Region { outer: Pt[]; holes: Pt[][] }

/**
 * Marching squares over an n x n coverage field (0..1 per pixel, row 0 at the top), in sheet coordinates.
 * Outer rings come out anticlockwise and holes clockwise, so a nonzero fill draws the holes open.
 */
export function traceField(v: ArrayLike<number>, n: number, tol = 0.0012): Region[] {
  const at = (i: number, j: number) => (i < 0 || j < 0 || i >= n || j >= n ? 0 : v[j * n + i]);
  const sx = (i: number) => ((i + 0.5) / n) * 2 - 1, sy = (j: number) => 1 - ((j + 0.5) / n) * 2;
  // a crossing on the edge from pixel (i0,j0) to (i1,j1), keyed by that edge
  const pts = new Map<string, Pt>();
  const cross = (i0: number, j0: number, i1: number, j1: number): string => {
    const key = `${i0},${j0},${i1},${j1}`;
    if (!pts.has(key)) {
      const a = at(i0, j0), b = at(i1, j1), t = Math.abs(b - a) < 1e-6 ? 0.5 : (0.5 - a) / (b - a);
      pts.set(key, [sx(i0 + (i1 - i0) * t), sy(j0 + (j1 - j0) * t)]);
    }
    return key;
  };
  const links = new Map<string, string[]>();
  const link = (p: string, q: string) => { (links.get(p) ?? links.set(p, []).get(p)!).push(q); (links.get(q) ?? links.set(q, []).get(q)!).push(p); };
  for (let j = -1; j < n; j++) {
    for (let i = -1; i < n; i++) {
      const a = at(i, j) >= 0.5, b = at(i + 1, j) >= 0.5, c = at(i + 1, j + 1) >= 0.5, d = at(i, j + 1) >= 0.5;
      if (a === b && b === c && c === d) continue;
      const top = a !== b ? cross(i, j, i + 1, j) : '', right = b !== c ? cross(i + 1, j, i + 1, j + 1) : '';
      const bottom = d !== c ? cross(i, j + 1, i + 1, j + 1) : '', left = a !== d ? cross(i, j, i, j + 1) : '';
      if (top && right && bottom && left) {
        // a saddle: the middle decides which pair of corners joins up
        const mid = (at(i, j) + at(i + 1, j) + at(i + 1, j + 1) + at(i, j + 1)) / 4 >= 0.5;
        if (mid === a) { link(top, right); link(bottom, left); } else { link(top, left); link(right, bottom); }
      } else {
        const e = [top, right, bottom, left].filter(Boolean);
        link(e[0], e[1]);
      }
    }
  }
  // walk the links into closed rings
  const seen = new Set<string>(), rings: Pt[][] = [];
  for (const start of links.keys()) {
    if (seen.has(start)) continue;
    const ring: Pt[] = [];
    let prev = '', cur = start;
    while (cur && !seen.has(cur)) {
      seen.add(cur);
      ring.push(pts.get(cur)!);
      const next = links.get(cur)!.find(q => q !== prev && !seen.has(q)) ?? '';
      prev = cur;
      cur = next;
    }
    if (ring.length > 2) rings.push(ring);
  }
  // nesting decides outer from hole; each hole goes to the smallest outer round it
  const info = rings.map(r => ({ r, area: area(r), depth: 0 }));
  for (const x of info) for (const y of info) if (x !== y && Math.abs(y.area) > Math.abs(x.area) && inside(x.r[0], y.r)) x.depth++;
  const outers = info.filter(x => x.depth % 2 === 0 && Math.abs(x.area) > 4e-5);
  const out = new Map(outers.map(o => [o, { outer: orient(simplify(o.r, tol), 1), holes: [] as Pt[][] }]));
  for (const h of info) {
    if (h.depth % 2 === 0 || Math.abs(h.area) < 2e-5) continue;
    let best: (typeof outers)[number] | null = null;
    for (const o of outers) if (o.depth === h.depth - 1 && inside(h.r[0], o.r) && (!best || Math.abs(o.area) < Math.abs(best.area))) best = o;
    if (best) out.get(best)!.holes.push(orient(simplify(h.r, tol), -1));
  }
  return [...out.values()].filter(r => r.outer.length > 2);
}

export function area(r: Pt[]): number {
  let s = 0;
  for (let i = 0, j = r.length - 1; i < r.length; j = i++) s += (r[j][0] - r[i][0]) * (r[j][1] + r[i][1]);
  return s / 2;
}

function orient(r: Pt[], sign: 1 | -1): Pt[] {
  return Math.sign(area(r)) === sign ? r : r.slice().reverse();
}

export function inside(p: Pt, r: Pt[]): boolean {
  let c = false;
  for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
    const [xi, yi] = r[i], [xj, yj] = r[j];
    if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) c = !c;
  }
  return c;
}

export const inRegion = (p: Pt, outer: Pt[], holes: Pt[][] = []) => inside(p, outer) && !holes.some(h => inside(p, h));

/** Douglas-Peucker on a closed ring, split at its two most distant points */
function simplify(r: Pt[], tol: number): Pt[] {
  if (r.length < 8) return r.map(round);
  let far = 0, fd = -1;
  for (let i = 1; i < r.length; i++) { const d = (r[i][0] - r[0][0]) ** 2 + (r[i][1] - r[0][1]) ** 2; if (d > fd) { fd = d; far = i; } }
  const a = dp(r.slice(0, far + 1), tol), b = dp([...r.slice(far), r[0]], tol);
  return [...a.slice(0, -1), ...b.slice(0, -1)].map(round);
}

function dp(p: Pt[], tol: number): Pt[] {
  if (p.length < 3) return p;
  const [x0, y0] = p[0], [x1, y1] = p[p.length - 1], dx = x1 - x0, dy = y1 - y0, len = Math.hypot(dx, dy) || 1e-9;
  let idx = 0, md = 0;
  for (let i = 1; i < p.length - 1; i++) {
    const d = Math.abs(dy * p[i][0] - dx * p[i][1] + x1 * y0 - y1 * x0) / len;
    if (d > md) { md = d; idx = i; }
  }
  if (md <= tol) return [p[0], p[p.length - 1]];
  const a = dp(p.slice(0, idx + 1), tol), b = dp(p.slice(idx), tol);
  return [...a.slice(0, -1), ...b];
}

const round = (p: Pt): Pt => [+p[0].toFixed(4), +p[1].toFixed(4)];
